import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBreakpoints } from "~/hooks/useBreakpoints";
import { toBool } from "~/utils/helpers";

import { Icon } from "../icon/Icon";
import { SheetModal } from "../sheetModal/SheetModal";
import { DialogModal } from "../dialogModal/DialogModal";

import "./DropdownMenu.css";

import type { ReactNode, CSSProperties, MouseEvent } from "react";
import type { IntentProp, SizeProp } from "../components";
import type { IconProps } from "../icon/Icon";

const DEFAULT_ICON = "angle-down";
const DEFAULT_PLACEHOLDER = "Select...";

export interface DropdownMenuStateAndHelpers {
  close: () => void;
}

export interface DropdownMenuProps {
  children?:
    | ReactNode
    | ((stateAndHelpers: DropdownMenuStateAndHelpers) => ReactNode);
  disabled?: boolean;
  defaultOpen?: boolean;
  icon?: IconProps["icon"];
  iconVariant?: IconProps["variant"];
  intent?: Extract<IntentProp, "success" | "error">;
  label: ReactNode;
  placeholder?: ReactNode;
  size?: Extract<SizeProp, "sm" | "md" | "lg">;
  style?: CSSProperties;
  onClose?: () => void;
  onOpen?: () => void;
}

export function DropdownMenu({
  children,
  defaultOpen,
  disabled,
  icon = DEFAULT_ICON,
  iconVariant,
  intent,
  label,
  placeholder = DEFAULT_PLACEHOLDER,
  size,
  style,
  onClose,
  onOpen,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const breakpoints = useBreakpoints();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleButtonClick = useCallback((e: MouseEvent) => {
    if (toBool(e.currentTarget.parentElement?.ariaDisabled)) {
      e.preventDefault();
      return;
    }

    setIsOpen((x) => !x);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const stateAndHelpers: DropdownMenuStateAndHelpers = useMemo(() => {
    return {
      close: handleCloseModal,
    };
  }, [handleCloseModal]);

  const content = useMemo(() => {
    if (typeof children === "function") {
      return children?.(stateAndHelpers);
    }

    return children;
  }, [children, stateAndHelpers]);

  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    } else {
      onClose?.();
    }
  }, [isOpen, onClose, onOpen]);

  return (
    <div
      className="DropdownMenu"
      aria-disabled={disabled}
      data-has-placeholder={!label}
      data-size={size}
      role="menu"
      style={style}
    >
      <button
        ref={buttonRef}
        className="DropdownMenu__button"
        onClick={handleButtonClick}
      >
        {intent && (
          <Icon
            icon={intent === "success" ? "circle-check" : "circle-xmark"}
            color={
              intent === "success"
                ? "var(--color-success)"
                : "var(--color-error)"
            }
            variant="solid"
          />
        )}

        <span className="DropdownMenu__button-label">
          {label ?? placeholder}
        </span>

        <Icon icon={icon} variant={iconVariant} />
      </button>

      {breakpoints.lg ? (
        <DialogModal
          isOpen={isOpen}
          size="sm"
          onRequestClose={handleCloseModal}
        >
          <DialogModal.Body>{content}</DialogModal.Body>
        </DialogModal>
      ) : (
        <SheetModal
          isOpen={isOpen}
          scrollable
          onRequestClose={handleCloseModal}
        >
          <SheetModal.Body>{content}</SheetModal.Body>
        </SheetModal>
      )}
    </div>
  );
}
